import { defineStore } from 'pinia'
import { STORE_ID } from '~/constants/store'
import type { PaymentMethodType, PaymentState as State, SupportedPaymentMethod } from '~/types/payment'
import type { PaymentMethod, PaymentResponseAction } from '~/types/api/data-contracts'
import useOrderStore from '~/store/order'
import useRestaurantStore from './restaurant'
import { OnlineOrderType } from '~/types/restaurant'
import { isGooglePayPaymentType } from '~/utils/payment'

export function getSupportedCardPaymentTypes(paymentMethods: PaymentMethod[]): string[] {
  const schemePaymentMethod = paymentMethods.find((item) => item.type === 'scheme')

  return schemePaymentMethod ? ['scheme', 'card', ...(schemePaymentMethod.brands ?? [])] : []
}

const initialState = (): State => ({
  checkoutRequestPaymentString: undefined,
  browserInfoJson: undefined,
  riskDataJson: undefined,
  paymentMethod: undefined,
  rawPaymentMethodsResponse: undefined,
  schemeBrands: [],
  paymentMethodsList: [],
  paymentMethodTitle: 'Add Payment',
  paymentMethodType: undefined,
  cardConfirmed: false,
  fetching: {
    paymentMethods: false
  },
  fetchDidFail: {
    paymentMethods: false
  },
  paymentAction: undefined,
  additionalDetailsJson: undefined,
  threeDSComponentTypeLoaded: undefined
})

/**
 * @description Store for payment related state used in the checkout
 */
const usePaymentStore = defineStore(STORE_ID.PAYMENT, {
  state: initialState,

  getters: {
    hasPaymentMethod(): boolean {
      return !!(this.paymentMethod && Object.keys(this.paymentMethod).length !== 0)
    },
    isPaymentGooglePay(): boolean {
      return isGooglePayPaymentType(this.paymentMethodType)
    },
    isPaymentApplePay(): boolean {
      return this.paymentMethodType === 'applepay'
    },
    isPaymentCard(): boolean {
      if (!this.paymentMethodType) return false

      return this.schemeBrands.includes(this.paymentMethodType)
    },
    getPaymentMethods(): PaymentMethodType[] {
      return this.paymentMethodsList
    }
  },

  actions: {
    setPaymentMethod({
      paymentMethod,
      title,
      type
    }: {
      paymentMethod: PaymentMethod
      title: string
      type: SupportedPaymentMethod
    }): void {
      const orderStore = useOrderStore()
      const restaurantStore = useRestaurantStore()

      if (this.paymentMethodType !== type) {
        this.clearCheckoutPaymentData()
      }

      this.paymentMethod = paymentMethod
      this.paymentMethodTitle = title
      this.paymentMethodType = type

      const restaurantName = restaurantStore.getSelectedRestaurant ? restaurantStore.getSelectedRestaurant.name : ''
      const restaurantId = restaurantStore.getSelectedRestaurant
        ? restaurantStore.getSelectedRestaurant.id.toString()
        : ''

      gaEventAddShippingInfo({
        currency: 'AUD',
        value: orderStore.cart ? orderStore.cart.priceInCents! / 100 : 0,
        shipping_tier: 'Ground',
        orderType: OnlineOrderType[restaurantStore.orderConfig.orderType],
        restaurantName,
        allergens: cartHasAllergens(orderStore.cart).toString(),
        items: getECommerceEventItems(orderStore.cart?.items, restaurantId)
      })
      gaEventAddPaymentInfo({
        currency: 'AUD',
        value: orderStore.cart ? orderStore.cart.priceInCents! / 100 : 0,
        payment_type: 'applepay',
        orderType: OnlineOrderType[restaurantStore.orderConfig.orderType],
        restaurantName,
        allergens: cartHasAllergens(orderStore.cart).toString(),
        items: getECommerceEventItems(orderStore.cart?.items, restaurantId)
      })
    },
    /**
     * @description Format payment request body for checkout API request
     * @param paymentData {PaymentMethod} - Payment method data
     */
    setCheckoutPaymentString(
      paymentMethod:
        | PaymentMethod
        | {
            type: string
            applePayToken: string
          }
        | {
            type: string
            googlePayToken: string
          }
    ): void {
      this.checkoutRequestPaymentString = JSON.stringify(paymentMethod)
    },
    setBrowserInfoJson(browserInfoJson: string | undefined): void {
      this.browserInfoJson = browserInfoJson
    },
    setRiskDataJson(riskDataJson: string | undefined): void {
      this.riskDataJson = riskDataJson
    },
    setCardConfirmed(cardConfirmed: boolean): void {
      this.cardConfirmed = cardConfirmed
    },
    setPaymentMethods(paymentMethods: PaymentMethodType[]): void {
      this.paymentMethodsList = paymentMethods
    },
    setPaymentMethodType(paymentMethodType: SupportedPaymentMethod): void {
      if (this.paymentMethodType !== paymentMethodType) {
        this.clearCheckoutPaymentData()
      }
      this.paymentMethodType = paymentMethodType
    },
    setPaymentAction(paymentAction?: PaymentResponseAction): void {
      this.paymentAction = paymentAction
    },
    setAdditionalDetailsJson(additionalDetailsJson: string | undefined): void {
      this.additionalDetailsJson = additionalDetailsJson
    },
    clearCheckoutPaymentData(): void {
      this.checkoutRequestPaymentString = undefined
      this.browserInfoJson = undefined
      this.riskDataJson = undefined
      this.additionalDetailsJson = undefined
    },
    async fetchPaymentMethods(): Promise<void> {
      const orderStore = useOrderStore()

      this.fetching.paymentMethods = true
      try {
        const res = await preparePaymentMethods(orderStore.currentCartId)
        this.rawPaymentMethodsResponse = res

        const paymentMethods = res.paymentMethods ?? []
        this.schemeBrands = getSupportedCardPaymentTypes(paymentMethods)

        const list = paymentMethods.filter((item) => item.type !== 'scheme') as PaymentMethodType[]

        const finalList = isPlatform('ios') ? list.filter((item) => !isGooglePayPaymentType(item.type)) : list
        this.setPaymentMethods(finalList)
        this.fetchDidFail.paymentMethods = false
      } catch (error) {
        this.fetchDidFail.paymentMethods = true
        console.warn('Error fetching payment methods', error)
        throw error
      } finally {
        this.fetching.paymentMethods = false
      }
    },
    resetSelectedPayment() {
      this.paymentMethodTitle = 'Add Payment'
      this.paymentMethodType = undefined
      this.paymentMethod = undefined
      this.clearCheckoutPaymentData()
      this.cardConfirmed = false
      this.schemeBrands = []
    }
  },
  persist: false
})

export default usePaymentStore
export { initialState }
